import React, { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import styled from 'styled-components';

import {
  AchRelationshipFundingMethod,
  FundYourAccountView,
  FundYourAccountViews,
  AchRelationshipFundingMethods,
} from '../FundYourAccount';
import { useApi } from '../../../context';
import { useData, useStateSelector } from '../../../state/selectors';
import { DefaultApplicationModel } from '../../../types';
import config from '../../../config';
import { Button, Loading, Text } from '@tradingblock/components';
import PlaidAchRelationship from '../components/PlaidAchRelationship';
import { AchRelationship, ApiResponse } from '@tradingblock/types';
import MicroDepositsAchRelationship from '../components/MicroDepositsAchRelationship';

const StyledFundingMethodSetupRequestStep = styled.div`
  .loading-container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .notes {
    margin-top: 1.5rem;
  }

  .fa-check-circle {
    color: var(--teal);
    font-size: 3em;
  }

  .congratulations, .cancel-request {
    margin-bottom: 0;
  }

  .bank-account-detail {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--text);
    margin-top: 1rem;
  }

  .bank-account-details {
    width: 100%;
    margin-top: 1rem;
  }

  .bank-account-details:first-child {
    border-top: 1px solid var(--text);
  }

  .bank-account-details:last-child {
    border-top: 1px solid var(--text);
  }

  .progress-stepper-navigation {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .btn-link {
    text-transform: unset;
    text-decoration: unset;
    letter-spacing: unset;
    font-weight: unset;
  }

  .request-submitted {
    text-align: center;
  }

  .bank-account-detail-label {
    padding-right: 2rem;
  }

  .bank-account-detail-value-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .bank-account-detail-value {
    width: fit-content;
  }

  .btn-link-solo {
    padding-right: unset;
    width: fit-content;
  }
`;

interface FundingMethodSetupRequestStepProps {
  setCurrentFundYourAccountView: Dispatch<SetStateAction<FundYourAccountView>>;
  selectedAchRelationshipFundingMethod: AchRelationshipFundingMethod | null;
  workingOrCompletedAchRelationship: AchRelationship | null;
  getAchRelationshipsData: KeyedMutator<ApiResponse<AchRelationship[]> | null>;
}

const FundingMethodSetupRequestStep: FunctionComponent<FundingMethodSetupRequestStepProps> = ({
  setCurrentFundYourAccountView,
  selectedAchRelationshipFundingMethod,
  workingOrCompletedAchRelationship,
  getAchRelationshipsData,
}) => {
  const api = useApi();

  const application = useData(d => d.data.application);

  const { accountId } = application || DefaultApplicationModel;

  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);

  const siteGrp = useStateSelector(s => s.ui.siteGrp);

  const {
    isLoading: isLoadingPlaidLinkTokenData,
    data: getPlaidLinkTokenResponse,
    error: errorGettingPlaidLinkTokenData
  } = useSWR(
    accountId &&
    selectedAchRelationshipFundingMethod === AchRelationshipFundingMethods.AchRelationshipViaPlaid &&
    !workingOrCompletedAchRelationship ?
    `${config.tradingApi}/Cashiering/${accountId}/AchRelationships/PlaidLinkToken` :
    null,
    () => {
      if (accountId) {
        return api.cashiering.ach.fetchPlaidLinkToken(accountId);
      }

      return null;
    },
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (
      getPlaidLinkTokenResponse &&
      getPlaidLinkTokenResponse.responseCode === 0 &&
      getPlaidLinkTokenResponse.payload &&
      getPlaidLinkTokenResponse.payload.linkToken
    ) {
      setPlaidLinkToken(getPlaidLinkTokenResponse.payload.linkToken);
    }
  }, [getPlaidLinkTokenResponse]);

  if (workingOrCompletedAchRelationship) {
    return (
      <StyledFundingMethodSetupRequestStep>
        <div className="d-flex justify-content-center align-items-center flex-direction-col">
          <i className="fal fa-check-circle m-1" />

          <p className="congratulations">
            <Text id="fundYourAccountComponent.congratulations" />
          </p>

          <p className="request-submitted">
            <Text id="fundYourAccountComponent.fundingMethodRequestSubmitted" />
          </p>

          <p>
            <Text
              id="fundYourAccountComponent.checkStatusOfRequest"
              data={{ requestType: <Text id="fundYourAccountComponent.fundingMethod" />}}
            />
          </p>

          <p className="cancel-request">
            <Text id="fundYourAccountComponent.cancelFundingMethodRequest" />
          </p>

          <div className="bank-account-details">
            <div className="bank-account-detail">
              <p className="bank-account-detail-label"><Text id="fundYourAccountComponent.bankName" /></p>
              <div className="bank-account-detail-value-wrapper">
                <p className="bank-account-detail-value">{workingOrCompletedAchRelationship.bankName}</p>
              </div>
            </div>

            <div className="bank-account-detail">
              <p className="bank-account-detail-label"><Text id="fundYourAccountComponent.accountType" /></p>
              <div className="bank-account-detail-value-wrapper">
                <p className="bank-account-detail-value">{workingOrCompletedAchRelationship.bankAccountType}</p>
              </div>
            </div>

            <div className="bank-account-detail">
              <p className="bank-account-detail-label"><Text id="fundYourAccountComponent.accountNickname" /></p>
              <div className="bank-account-detail-value-wrapper">
                <p className="bank-account-detail-value">{workingOrCompletedAchRelationship.nickName}</p>
              </div>
            </div>

            <div className="bank-account-detail">
              <p className="bank-account-detail-label"><Text id="fundYourAccountComponent.accountNumber" /></p>
              <div className="bank-account-detail-value-wrapper">
                <p className="bank-account-detail-value">{workingOrCompletedAchRelationship.bankAccount}</p>
              </div>
            </div>

            <div className="bank-account-detail">
              <p className="bank-account-detail-label"><Text id="fundYourAccountComponent.routingNumber" /></p>
              <div className="bank-account-detail-value-wrapper">
                <p className="bank-account-detail-value">{workingOrCompletedAchRelationship.bankRoutingNumber}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="progress-stepper-navigation">
          <Button
            className="btn-link"
            onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingMethods)}
          >
            <Text id="btns.backToFundingMethods" />
          </Button>

          {
            workingOrCompletedAchRelationship &&
            <Button
              className="btn-primary"
              onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingRequestStep)}
            >
              <Text id="btns.next" />
            </Button>
          }
        </div>
      </StyledFundingMethodSetupRequestStep>
    );
  }

  if (
    !isLoadingPlaidLinkTokenData &&
    selectedAchRelationshipFundingMethod === AchRelationshipFundingMethods.AchRelationshipViaPlaid &&
    (
      errorGettingPlaidLinkTokenData ||
      getPlaidLinkTokenResponse === null ||
      (
        getPlaidLinkTokenResponse &&
        (
          getPlaidLinkTokenResponse.responseCode !== 0 ||
          !getPlaidLinkTokenResponse.payload ||
          !getPlaidLinkTokenResponse.payload.linkToken
        )
      )
    )
  ) {
    return (
      <StyledFundingMethodSetupRequestStep>
        <div className="d-flex justify-content-center align-items-center flex-direction-col">
          <i className="fal fa-exclamation-circle error m-1" />
          <p className="issue-on-our-end">
            <Text id="fundYourAccountComponent.issueOnOurEnd" type="error" />
          </p>
        </div>

        <div className="progress-stepper-navigation">
          <Button
            className="btn-link btn-link-solo"
            onClick={() => setCurrentFundYourAccountView(FundYourAccountViews.FundingMethods)}
          >
            <Text id="btns.backToFundingMethods" />
          </Button>
        </div>
      </StyledFundingMethodSetupRequestStep>
    );
  }

  return (
    <StyledFundingMethodSetupRequestStep>
      {isLoadingPlaidLinkTokenData && <div className="loading-container"><Loading /></div>}

      {
        !isLoadingPlaidLinkTokenData &&
        (
          <div className="notes">
            <p><Text id="fundYourAccountComponent.fundingMethodSetupRequestStepNotes" /></p>
            <p><Text id="fundYourAccountComponent.makeSureBankAccountInfoMatches" siteGroup={siteGrp} /></p>
            <p><Text id="fundYourAccountComponent.canOnlyLinkCheckingAndSavingAccounts" /></p>
          </div>
        )
      }

      {
        !isLoadingPlaidLinkTokenData &&
        selectedAchRelationshipFundingMethod === AchRelationshipFundingMethods.AchRelationshipViaPlaid &&
        <PlaidAchRelationship plaidLinkToken={plaidLinkToken} getAchRelationshipsData={getAchRelationshipsData} setCurrentFundYourAccountView={setCurrentFundYourAccountView} />
      }

      {
        selectedAchRelationshipFundingMethod === AchRelationshipFundingMethods.AchRelationshipViaMicroDeposits &&
        <MicroDepositsAchRelationship getAchRelationshipsData={getAchRelationshipsData} setCurrentFundYourAccountView={setCurrentFundYourAccountView} />
      }
    </StyledFundingMethodSetupRequestStep>
  );
};

export default FundingMethodSetupRequestStep;